import { createSlice } from '@reduxjs/toolkit';
import {
  LoadCheckoutResponse,
  BillingRequest,
  Message,
} from './Checkout.interface';
import { isNotEmptyObject } from '@next-app/utils/common-utils';
import { BillingAddressFormInitialState } from '@next-app/components/Checkout/BillingAddressForm/BillingAddressFormFields';

interface CheckoutInitState {
  checkoutLoadDetails: LoadCheckoutResponse | {};
  billingRequest: BillingRequest;
  isSubmitOrder: boolean;
  giftItemFrom: string;
  isGiftItemAnonymous: boolean;
  checkoutAdditionalMsg: Message;
  paymentMethodType: string;
  reloadLoadCheckoutDetails: boolean;
  sameAsDeliveryAddress: boolean;
  orderComments: string;
  submitError: string;
}

const checkoutState = {
  checkoutLoadDetails: {},
  checkoutAdditionalMsg: {},
  billingRequest: {
    poaFormData: {
      poNumber: '',
      taxExempt: false,
      paymentMethod: '',
      repositoryId: '',
      ownerId: '',
    },
    billingAddressFormData: {},
    creditCardFormData: {
      accountNumber: '',
      cardHolderName: '',
      expirationMonth: '',
      expirationYear: 0,
      creditCardNumber: '',
      poNumber: '',
      saveAsDefault: false,
      savePayment: false,
      cardVerificationNumber: '',
      paymentMethod: 'creditCard',
      useShippingAddress: false,
    },
    poaFormErrors: {},
    billingAddressForErrors: {},
    creditCardFormErrors: {},
  },
  isSubmitOrder: true,
  giftItemFrom: '',
  isGiftItemAnonymous: false,
  paymentMethodType: '',
  reloadLoadCheckoutDetails: false,
  sameAsDeliveryAddress: false,
  orderComments: '',
  submitError: '',
} satisfies CheckoutInitState as CheckoutInitState;

export const CheckoutSlice = createSlice({
  name: 'checkout',
  initialState: checkoutState,
  reducers: {
    setLoadCheckoutDetails(state, action) {
      state.checkoutLoadDetails = action.payload;
    },
    setBillingRequest: (state, action) => {
      state.billingRequest = {
        ...state.billingRequest,
        ...(action.payload.billingAddressFormData !== undefined && {
          billingAddressFormData: isNotEmptyObject(
            action.payload.billingAddressFormData,
          )
            ? {
                ...state.billingRequest.billingAddressFormData,
                ...action.payload.billingAddressFormData,
              }
            : BillingAddressFormInitialState,
        }),
        poaFormData: {
          ...state.billingRequest?.poaFormData, // Preserve poaFormData fields
          ...action.payload.poaFormData, // Update only provided fields
        },
        creditCardFormData: {
          ...state.billingRequest?.creditCardFormData,
          ...action.payload.creditCardFormData,
        },
        billingAddressForErrors: {
          ...state.billingRequest?.billingAddressForErrors,
          ...action.payload.billingAddressForErrors,
        },
      };
    },
    setIsSubmitOrder: (state, action) => {
      state.isSubmitOrder = action.payload;
    },
    setLoadCheckoutAdditionalMsg(state, action) {
      state.checkoutAdditionalMsg = action.payload;
    },
    setGiftingItemFrom(state, action) {
      state.giftItemFrom = action.payload;
    },
    setGiftItemIsAnonymous(state, action) {
      state.isGiftItemAnonymous = action.payload;
    },
    setPaymentMethodType: (state, action) => {
      state.paymentMethodType = action.payload;
    },
    setSameAsDeliveryAddress: (state, action) => {
      state.sameAsDeliveryAddress = action.payload;
    },
    setBillingAddressError: (state, action) => {
      if (isNotEmptyObject(action.payload)) {
        state.billingRequest = {
          ...state.billingRequest,
          billingAddressForErrors: {
            ...state.billingRequest?.billingAddressForErrors,
            ...action.payload,
          },
        };
      } else {
        state.billingRequest = {
          ...state.billingRequest,
          billingAddressForErrors: {},
        };
      }
    },
    setReloadLoadCheckoutDetails: (state, action) => {
      state.reloadLoadCheckoutDetails = action.payload;
    },
    setCreditCardFormError: (state, action) => {
      state.billingRequest.creditCardFormErrors = action.payload;
    },
    setOrderComments: (state, action) => {
      state.orderComments = action.payload;
    },
    setSubmitOrderError: (state, action) => {
      state.submitError = action.payload;
    },
  },
});

export const {
  setLoadCheckoutDetails,
  setBillingRequest,
  setIsSubmitOrder,
  setGiftingItemFrom,
  setGiftItemIsAnonymous,
  setLoadCheckoutAdditionalMsg,
  setPaymentMethodType,
  setBillingAddressError,
  setReloadLoadCheckoutDetails,
  setSameAsDeliveryAddress,
  setCreditCardFormError,
  setOrderComments,
  setSubmitOrderError,
} = CheckoutSlice.actions;

export default CheckoutSlice.reducer;
